import { getAppIdFromPath, getAppIdFromRouteName } from '@common/app-access-layer/config/app-visibility.config'

// Event names match the revex ES indices verbatim (incl. the `communites` typo).
export const PAGE_HIT = {
  CLIENTPORTAL: 'clientportal_user_page_hit',
  GOKOLLAB: 'gokollab_user_page_hit',
  COMMUNITIES: 'communites_user_page_hit', // [sic]
  COURSES: 'courses_learner_page_hit',
  /** Courses `/courses/*` page views when the learner library query cache is present. */
  LEARNER_V2: 'learner_page_hit_v2',
} as const

export type PageHitEvent = (typeof PAGE_HIT)[keyof typeof PAGE_HIT]

/** TanStack query key for the learner library product list (`library.query-store`). */
export const libraryListQueryKey = (locationId: string) => ['library', locationId, 'list'] as const

export function resolveAppFlavor(appName: unknown): string {
  const normalized = typeof appName === 'string' ? appName.trim().toLowerCase() : ''
  return normalized || 'clientportal'
}

/** True for `/courses` and `/courses/...` (literal path prefix — not gokollab slug routes). */
export function isCoursesRoutePath(path: string): boolean {
  return path === '/courses' || path.startsWith('/courses/')
}

/** Library cache is considered present once the list query has resolved to an array (including empty). */
export function hasCachedLibraryData(locationId: string, getQueryData: (key: readonly unknown[]) => unknown): boolean {
  if (!locationId) return false
  return Array.isArray(getQueryData(libraryListQueryKey(locationId)))
}

export interface ResolveEventTypesOptions {
  /** When true and path is `/courses/*`, also emit `learner_page_hit_v2`. */
  hasLibraryData?: boolean
}

// Match on path OR route name — gokollab serves communities/courses under slugs (no /communities path).
export function resolveEventTypes(
  path: string,
  routeName: string | undefined,
  isGokollab: boolean,
  options?: ResolveEventTypesOptions
): PageHitEvent[] {
  const name = routeName ?? ''
  const isCommunities = path.startsWith('/communities') || name.includes('communities')
  const isCourses = path.startsWith('/courses') || name.includes('courses')

  const events: PageHitEvent[] = [PAGE_HIT.CLIENTPORTAL]
  if (isGokollab) events.push(PAGE_HIT.GOKOLLAB)
  if (isCommunities) events.push(PAGE_HIT.COMMUNITIES)
  if (isCourses) events.push(PAGE_HIT.COURSES)
  if (options?.hasLibraryData && isCoursesRoutePath(path)) {
    events.push(PAGE_HIT.LEARNER_V2)
  }
  return events
}

// /account appends its active tab so each tab counts as a distinct page.
export function buildTrackedRoute(path: string, activeTab?: string): string {
  return path === '/account' && activeTab ? `${path}-${activeTab}` : path
}

// Sidebar section for a route — by path prefix (ClientPortal) or route name (GoKollab tenant slugs),
// covering every appId. Dashboard/home and unmapped routes fall back to the app flavor (clientportal | gokollab | courses).
export function resolveAppType(path: string, routeName: string | undefined, flavor: string): string {
  const appId = getAppIdFromPath(path) ?? getAppIdFromRouteName(routeName)
  return appId && appId !== 'dashboard' ? appId : flavor
}
